// src/services/emailDomainValidator.ts
import * as mailchecker from 'mailchecker';

export interface EmailDomainValidationResult {
  isValid: boolean;
  error: string;
  domain: string;
  isDisposable: boolean;
  isInAllowedList: boolean;
  isBlacklisted: boolean;
}

class EmailDomainValidator {
  // Lista eksplicitno dozvoljenih email domena (prolaze bez ikakve provere)
  private readonly allowedDomains: string[] = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'msn.com',
    'icloud.com',
    'protonmail.com',
    'proton.me',
    'fastmail.com',
    'zoho.com',
    'gmx.com',
    'mail.com',
    'aol.com',
    'yandex.com',
    'mail.ru',
    // Regional
    'mts.rs',
    'telenor.rs',
    'sbb.rs',
    'orion.rs',
  ];

  // Cache za GitHub blacklist (da ne loadujemo stalno)
  private blacklistCache: Set<string> | null = null;
  private blacklistCacheTime = 0;
  private readonly CACHE_TTL = 60 * 60 * 1000; // 1 sat

  /**
   * Učitava blacklist sa GitHub-a
   */
  private async loadGitHubBlacklist(): Promise<Set<string>> {
    const now = Date.now();

    // Ako imamo fresh cache, koristi ga
    if (this.blacklistCache && now - this.blacklistCacheTime < this.CACHE_TTL) {
      return this.blacklistCache;
    }

    try {
      console.log('Frontend - Učitavam GitHub blacklist...');

      // Disposable email domains (automatski ažuriran)
      const response = await fetch(
        'https://raw.githubusercontent.com/disposable/disposable-email-domains/master/domains.txt'
      );

      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status}`);
      }

      const text = await response.text();
      const domains = text
        .split('\n')
        .map((line) => line.trim().toLowerCase())
        .filter((line) => line && !line.startsWith('#'));

      this.blacklistCache = new Set(domains);
      this.blacklistCacheTime = now;

      console.log(
        `Frontend - Učitano ${domains.length} blacklist domena sa GitHub-a`
      );
      return this.blacklistCache;
    } catch (error) {
      console.warn('Frontend - Greška pri učitavanju GitHub blacklist:', error);

      // Fallback na prazan set ako nema interneta
      if (!this.blacklistCache) {
        this.blacklistCache = new Set();
      }
      return this.blacklistCache;
    }
  }

  /**
   * Sinhrona provera da li je domen u GitHub blacklist-i
   */
  private isInGitHubBlacklist(domain: string): boolean {
    if (!this.blacklistCache) {
      // Ako nema cache-a, ne blokira (graceful degradation)
      return false;
    }
    return this.blacklistCache.has(domain.toLowerCase());
  }

  /**
   * Asinhrona inicijalizacija blacklist-e
   */
  public async initializeBlacklist(): Promise<void> {
    await this.loadGitHubBlacklist();
  }

  /**
   * Glavna logika validacije email domena
   * REDOSLED PROVERA:
   * 1. Eksplicitno dozvoljeni domeni → ODMAH DOZVOLI
   * 2. GitHub blacklist → BLOKIRA
   * 3. Mailchecker → BLOKIRA ako je disposable
   * 4. Sve ostalo → DOZVOLI
   */
  async validateEmailDomainAsync(
    email: string
  ): Promise<EmailDomainValidationResult> {
    if (!email || !email.includes('@')) {
      return {
        isValid: false,
        error: 'Neispravna email adresa',
        domain: '',
        isDisposable: false,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) {
      return {
        isValid: false,
        error: 'Neispravna email adresa',
        domain: '',
        isDisposable: false,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    console.log('Frontend - Proveravam email:', email);
    console.log('Frontend - Domen:', domain);

    // 1. EKSPLICITNO DOZVOLJENI - prolaze bez ikakve provere
    const isInAllowedList = this.allowedDomains.includes(domain);
    if (isInAllowedList) {
      console.log(
        'Frontend - DOZVOLJEN - eksplicitno u allowedDomains (preskačemo sve provere)'
      );
      return {
        isValid: true,
        error: '',
        domain,
        isDisposable: false,
        isInAllowedList: true,
        isBlacklisted: false,
      };
    }

    // 2. GITHUB BLACKLIST PROVERA
    await this.loadGitHubBlacklist(); // Ensure blacklist is loaded
    const isBlacklisted = this.isInGitHubBlacklist(domain);
    console.log('Frontend - GitHub blacklist:', isBlacklisted);

    if (isBlacklisted) {
      console.log('Frontend - BLOKIRAN - u GitHub blacklist-i');
      return {
        isValid: false,
        error: 'Ovaj email provajder nije dozvoljen (blacklist)',
        domain,
        isDisposable: true, // Tretiramo kao disposable
        isInAllowedList: false,
        isBlacklisted: true,
      };
    }

    // 3. MAILCHECKER PROVERA
    const isNotDisposable = mailchecker.isValid(email);
    const isDisposable = !isNotDisposable;
    console.log(
      'Frontend - Mailchecker (true = nije disposable):',
      isNotDisposable
    );

    if (isDisposable) {
      console.log('Frontend - BLOKIRAN - mailchecker kaže da je disposable');
      return {
        isValid: false,
        error: 'Disposable email adrese nisu dozvoljene',
        domain,
        isDisposable: true,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    // 4. SVI OSTALI DOMENI - dozvoljeni
    console.log('Frontend - DOZVOLJEN - prošao sve provere');
    return {
      isValid: true,
      error: '',
      domain,
      isDisposable: false,
      isInAllowedList: false,
      isBlacklisted: false,
    };
  }

  /**
   * Sinhrona verzija za backward compatibility
   */
  validateEmailDomain(email: string): EmailDomainValidationResult {
    if (!email || !email.includes('@')) {
      return {
        isValid: false,
        error: 'Neispravna email adresa',
        domain: '',
        isDisposable: false,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) {
      return {
        isValid: false,
        error: 'Neispravna email adresa',
        domain: '',
        isDisposable: false,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    // 1. EKSPLICITNO DOZVOLJENI
    const isInAllowedList = this.allowedDomains.includes(domain);
    if (isInAllowedList) {
      return {
        isValid: true,
        error: '',
        domain,
        isDisposable: false,
        isInAllowedList: true,
        isBlacklisted: false,
      };
    }

    // 2. GITHUB BLACKLIST (sinhrono)
    const isBlacklisted = this.isInGitHubBlacklist(domain);
    if (isBlacklisted) {
      return {
        isValid: false,
        error: 'Ovaj email provajder nije dozvoljen (blacklist)',
        domain,
        isDisposable: true,
        isInAllowedList: false,
        isBlacklisted: true,
      };
    }

    // 3. MAILCHECKER
    const isNotDisposable = mailchecker.isValid(email);
    if (!isNotDisposable) {
      return {
        isValid: false,
        error: 'Disposable email adrese nisu dozvoljene',
        domain,
        isDisposable: true,
        isInAllowedList: false,
        isBlacklisted: false,
      };
    }

    // 4. DOZVOLJEN
    return {
      isValid: true,
      error: '',
      domain,
      isDisposable: false,
      isInAllowedList: false,
      isBlacklisted: false,
    };
  }

  /**
   * Brza provera (preporučeno za UI)
   */
  async isEmailDomainAllowed(email: string): Promise<boolean> {
    const result = await this.validateEmailDomainAsync(email);
    return result.isValid;
  }

  /**
   * Sinhrona brza provera
   */
  isEmailDomainAllowedSync(email: string): boolean {
    const result = this.validateEmailDomain(email);
    return result.isValid;
  }

  /**
   * Test funkcija za debug
   */
  async testEmailValidation(): Promise<void> {
    const testEmails = [
      'test@gmail.com', // Treba da prođe (allowedDomains)
      'test@protonmail.com', // Treba da prođe (allowedDomains)
      'test@10minutemail.com', // Treba da se blokira (GitHub blacklist)
      'test@guerrillamail.com', // Treba da se blokira (GitHub blacklist)
      'test@fastmail.com', // Treba da prođe (nije u allowed, ali mailchecker OK)
      'test@yopmail.com', // Treba da se blokira (mailchecker ili blacklist)
      'test@company.com', // Zavisi od mailchecker
    ];

    console.log('=== FRONTEND TESTIRANJE EMAIL VALIDACIJE (ENHANCED) ===');

    // Ensure blacklist is loaded
    await this.loadGitHubBlacklist();

    for (const email of testEmails) {
      const result = await this.validateEmailDomainAsync(email);
      console.log(
        `${email}: ${result.isValid ? 'DOZVOLJEN' : 'BLOKIRAN'} - ${
          result.error || 'OK'
        } (allowed: ${result.isInAllowedList}, blacklisted: ${
          result.isBlacklisted
        }, disposable: ${result.isDisposable})`
      );
    }
    console.log('=== KRAJ FRONTEND TESTIRANJA ===');
  }

  /**
   * Dobija listu dozvoljenih domena
   */
  getAllowedDomains(): string[] {
    return [...this.allowedDomains];
  }

  /**
   * Dobija broj blacklist domena (za debug)
   */
  getBlacklistSize(): number {
    return this.blacklistCache?.size || 0;
  }

  /**
   * Refresh blacklist manually
   */
  async refreshBlacklist(): Promise<void> {
    this.blacklistCache = null;
    this.blacklistCacheTime = 0;
    await this.loadGitHubBlacklist();
  }
}

// Singleton instance
const emailDomainValidator = new EmailDomainValidator();

// Asinhrona inicijalizacija u development mode
if (process.env.NODE_ENV === 'development') {
  emailDomainValidator.initializeBlacklist().then(() => {
    emailDomainValidator.testEmailValidation();
  });
}

export default emailDomainValidator;
